<template>
  <transition name="ctx-fade">
    <div
      v-if="visible"
      class="map-context-menu"
      :style="menuStyle"
      @click.stop
    >
      <!-- Coordinates display -->
      <div class="ctx-coords">
        <v-icon x-small :color="'var(--kln-text-dim)'" class="mr-1">mdi-map-marker</v-icon>
        <span class="ctx-coords-text">{{ formattedCoords }}</span>
      </div>

      <div class="ctx-divider" />

      <!-- Acción primaria: crear incidente en el punto del clic.
           Gateada por cad_permission_create_incident (permiso CAD que el
           operador necesita para POST a /api/v2/cad-incidents/create-incident). -->
      <button
        v-if="canCreateIncident"
        class="ctx-action ctx-action--primary"
        @click="createIncident"
      >
        <v-icon x-small class="ctx-action-icon">mdi-alert-plus</v-icon>
        <span>Crear incidente</span>
      </button>

      <div v-if="canCreateIncident" class="ctx-divider" />

      <!-- Acciones secundarias -->
      <button class="ctx-action" @click="copyCoords">
        <v-icon x-small class="ctx-action-icon">mdi-content-copy</v-icon>
        <span>Copiar coordenadas</span>
        <span v-if="copied" class="ctx-copied">¡Copiado!</span>
      </button>

      <button class="ctx-action" @click="findNearest">
        <v-icon x-small class="ctx-action-icon">mdi-car-emergency</v-icon>
        <span>Unidad más cercana</span>
      </button>

      <button class="ctx-action" @click="listNearby">
        <v-icon x-small class="ctx-action-icon">mdi-car-multiple</v-icon>
        <span>Ver unidades cercanas</span>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'MapContextMenu',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    coords: {
      type: Object,
      default: () => ({ lng: 0, lat: 0 }),
    },
    screenPosition: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
  },

  data() {
    return {
      copied: false,
      copyTimer: null,
    }
  },

  computed: {
    canCreateIncident() {
      const u = this.$store && this.$store.state && this.$store.state.auth
        ? this.$store.state.auth.user : null
      if (!u || !u.attributes) return false
      return !!u.attributes.cad_permission_create_incident
    },
    formattedCoords() {
      if (!this.coords) return '—'
      const lat = typeof this.coords.lat === 'number' ? this.coords.lat.toFixed(6) : '—'
      const lng = typeof this.coords.lng === 'number' ? this.coords.lng.toFixed(6) : '—'
      return lat + ', ' + lng
    },

    menuStyle() {
      if (!this.screenPosition) return {}
      const x = this.screenPosition.x || 0
      const y = this.screenPosition.y || 0

      // Offset diagonal de 14px para que el marker visual del clic
      // (renderizado en x,y exactos sobre el mapa) quede visible en la
      // esquina superior izquierda del menú, no tapado debajo.
      const OFFSET = 14
      // Keep menu within viewport bounds (approximate menu size: 210x150px)
      const menuW = 210
      const menuH = 150
      const vw = window.innerWidth
      const vh = window.innerHeight

      const left = Math.min(x + OFFSET, vw - menuW - 8)
      const top = Math.min(y + OFFSET, vh - menuH - 8)

      return {
        left: left + 'px',
        top: top + 'px',
      }
    },
  },

  watch: {
    visible(val) {
      if (!val) {
        this.copied = false
        if (this.copyTimer) {
          clearTimeout(this.copyTimer)
          this.copyTimer = null
        }
      }
    },
  },

  mounted() {
    document.addEventListener('click', this._onDocClick)
    document.addEventListener('keydown', this._onKeyDown)
  },

  beforeDestroy() {
    document.removeEventListener('click', this._onDocClick)
    document.removeEventListener('keydown', this._onKeyDown)
    if (this.copyTimer) clearTimeout(this.copyTimer)
  },

  methods: {
    _onDocClick() {
      if (this.visible) {
        this.$emit('close')
      }
    },

    _onKeyDown(e) {
      if (this.visible && e.key === 'Escape') {
        this.$emit('close')
      }
    },

    copyCoords() {
      const text = this.formattedCoords
      if (!text || text === '—') return

      // Use the Clipboard API safely
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this._showCopied()
        }).catch(() => {
          this._fallbackCopy(text)
        })
      } else {
        this._fallbackCopy(text)
      }
    },

    _fallbackCopy(text) {
      const el = document.createElement('textarea')
      el.value = text
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      try {
        document.execCommand('copy')
        this._showCopied()
      } catch {
        // ignore
      } finally {
        document.body.removeChild(el)
      }
    },

    _showCopied() {
      this.copied = true
      if (this.copyTimer) clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => {
        this.copied = false
        this.$emit('close')
      }, 1200)
    },

    findNearest() {
      this.$emit('nearest-unit', this.coords)
      this.$emit('close')
    },

    listNearby() {
      this.$emit('nearby-at-point', this.coords)
      this.$emit('close')
    },

    createIncident() {
      this.$emit('create-incident', this.coords)
      this.$emit('close')
    },
  },
}
</script>

<style scoped>
.map-context-menu {
  position: fixed;
  z-index: 9999;
  min-width: 210px;
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  pointer-events: all;
  user-select: none;
}

.ctx-coords {
  display: flex;
  align-items: center;
  padding: 10px 12px 8px;
}

.ctx-coords-text {
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 11px;
  color: var(--kln-text-muted);
  letter-spacing: 0.03em;
}

.ctx-divider {
  height: 1px;
  background: var(--kln-border-dim);
  margin: 0;
}

.ctx-action {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  color: var(--kln-text-muted);
  font-size: 12px;
  transition: background 0.15s, color 0.15s;
  outline: none;
  position: relative;
}

.ctx-action:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text);
}

.ctx-action:active {
  background: var(--kln-border-dim);
}

.ctx-action--primary {
  color: var(--kln-accent) !important;
  font-weight: 600;
}

.ctx-action--primary .ctx-action-icon {
  color: var(--kln-accent) !important;
}

.ctx-action-icon {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.ctx-copied {
  position: absolute;
  right: 12px;
  font-size: 10px;
  color: var(--kln-alert-success);
  font-weight: 600;
}

/* Transition */
.ctx-fade-enter-active,
.ctx-fade-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}

.ctx-fade-enter,
.ctx-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
